import { Injectable } from '@angular/core';
import type { AccessCardFindInterface } from '@zaoblako/shared/modules/pacs/dtos/access-card/access-card-find.dto';
import type { ExternalUserFindInterface } from '@zaoblako/shared/modules/security/dtos/external-user/external-user-find.dto';
import type { ExternalUserPartialFormInterface } from '@zaoblako/shared/modules/security/dtos/external-user/external-user-partial-form.dto';
import type { RoomAccessFindInterface } from '@zaoblako/shared/modules/security/dtos/room-access/room-access-find.dto';
import { ProfileStatusEnum } from '@zaoblako/shared/modules/security/enums/profile-status.enum';
import type { FileFindInterface } from '@zaoblako/shared/modules/storage/dtos/file-find.dto';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { AccessCardService } from '@zaoblako/web/core/services/control/access-card.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { ExternalUserService } from '@zaoblako/web/core/services/security/external-user.service';
import { ProfileService } from '@zaoblako/web/core/services/security/profile.service';
import { StoreService } from '@zaoblako/web/core/services/store.service';
import { TitleService } from '@zaoblako/web/core/services/title.service';
import { KeysOfType } from '@zaoblako/web/core/types/keys-of.type';
import { ExternalUserState } from '@zaoblako/web/modules/security/components/external-user/card/models/external-user-state';
import { externalUserInitialState } from '@zaoblako/web/modules/security/components/external-user/card/state/external-user-initial-state';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ExternalUserStoreService extends StoreService<ExternalUserState> {
  protected state = new BehaviorSubject(externalUserInitialState);

  public get getState() {
    return this.state.value;
  }

  public constructor(
    private readonly externalUserService: ExternalUserService,
    private readonly profileService: ProfileService,
    private readonly accessCardService: AccessCardService,
    private readonly titleService: TitleService,
    private readonly confirmService: ConfirmDialogService,
    private readonly errorService: ErrorService
  ) {
    super();
  }

  public loadUser(id: number) {
    this.updateState({
      loading: true
    });

    this.externalUserService.findById<ExternalUserFindInterface>(id, { withDeleted: true }).subscribe((user) => {
      this.updateState({
        loading: false,
        user
      });
      this.titleService.setTitle(user?.profile?.name);
    });
  }

  public invite(resetPassword: boolean = false) {
    this.updateState({
      loadingInvite: true
    });

    this.externalUserService.inviteOne(this.getState.user?.id, { resetPassword }).subscribe({
      next: () => {
        this.errorService.addSuccessMessage('Пользователю было выслано приглашение');
        this.updateState((state) => ({
          loadingInvite: false,
          user: {
            ...state.user,
            profile: {
              ...state.user.profile,
              status: ProfileStatusEnum.ACTIVE
            }
          }
        }));
      },
      error: () => {
        this.updateState({
          loadingInvite: false
        });
      }
    });
  }

  public generateTelegramLink() {
    if (this.getState.user.profile?.telegramInviteUrl?.length) {
      setTimeout(() => {
        navigator.clipboard
          .writeText(this.getState.user.profile.telegramInviteUrl)
          .then(() => {
            this.errorService.addSuccessMessage('Ссылка скопирована. Пришлите пользователю');
          })
          .catch((e) => {
            this.errorService.addCustomError('Ошибка', e);
          });
      }, 100);
    }
  }

  public leaveTelegram() {
    this.updateState({
      loadingInvite: true
    });

    this.profileService.leaveTelegramChat(this.getState.user?.profile?.id).subscribe({
      next: () => {
        this.errorService.addSuccessMessage('Telegram успешно отвязан');
        this.updateState((state) => ({
          loadingInvite: false,
          user: {
            ...state.user,
            profile: {
              ...state.user.profile,
              telegramId: null,
              isInvitedToTelegram: false
            }
          }
        }));
      },
      error: () => {
        this.updateState({
          loadingInvite: false
        });
      }
    });
  }

  public banUser() {
    this.confirmService.confirm({
      message: 'Вы действительно хотите заблокировать доступ пользователю?',
      accept: () => {
        this.updateState({ loadingChangeStatus: true });

        this.externalUserService.ban(this.getState.user?.id).subscribe({
          next: () => {
            this.updateState((state) => ({
              user: {
                ...state.user,
                banned: true
              },
              loadingChangeStatus: false
            }));
            this.errorService.addSuccessMessage('Пользователю заблокирован доступ к личному кабинету');
          },
          error: () => {
            this.updateState({ loadingChangeStatus: false });
          }
        });
      }
    });
  }

  public unbanUser() {
    this.confirmService.confirm({
      message: 'Вы действительно хотите разблокировать доступ пользователю?',
      accept: () => {
        this.updateState({ loadingChangeStatus: true });

        this.externalUserService.unban(this.getState.user?.id).subscribe({
          next: () => {
            this.updateState((state) => ({
              user: {
                ...state.user,
                banned: false
              },
              loadingChangeStatus: false
            }));
            this.errorService.addSuccessMessage('Пользователю разблокирован доступ к личному кабинету');
          },
          error: () => {
            this.updateState({ loadingChangeStatus: false });
          }
        });
      }
    });
  }

  public deleteUser() {
    this.confirmService.confirm({
      message: 'Вы действительно хотите заблокировать пользователя?',
      accept: () => {
        this.updateState({ loadingChangeStatus: true });

        this.externalUserService.deleteById<ExternalUserFindInterface>(this.getState.user?.id).subscribe({
          next: (user) => {
            this.updateState((state) => ({
              user: {
                ...state.user,
                deleted: user.deleted,
                banned: user.banned
              },
              loadingChangeStatus: false
            }));
            this.errorService.addSuccessMessage('Пользователь заблокирован');
          },
          error: () => {
            this.updateState({ loadingChangeStatus: false });
          }
        });
      }
    });
  }

  public onAvatarFileSelect(file: FileFindInterface) {
    this.updateState((state) => ({
      user: {
        ...state.user,
        profile: {
          ...state.user.profile,
          file
        }
      }
    }));
    this.profileService.update(this.getState.user?.profile).subscribe({
      next: () => {
        this.errorService.addSuccessMessage('Аватарка обновлена');
      }
    });
  }

  public updatePanelsInfo(key: KeysOfType<typeof this.getState.panelsInfo, any>, value: any): void {
    const info = this.getState.panelsInfo;
    info[key] = value;
    this.updateState({ panelsInfo: info });
    localStorage.setItem('external-user.panelsInfo', JSON.stringify(info));
  }

  public loadPanelsInfo(): void {
    if (localStorage.getItem('external-user.panelsInfo')) {
      const panelsInfo = JSON.parse(localStorage.getItem('external-user.panelsInfo'));
      Object.keys(this.getState.panelsInfo).forEach((key) => {
        panelsInfo[key] = panelsInfo.hasOwnProperty(key) ? panelsInfo[key] : this.getState.panelsInfo[key];
      });
      this.updateState({ panelsInfo });
    }
  }

  public loadAccessCards(): void {
    this.updateState((state: ExternalUserState) => ({
      accessCardsInfo: {
        ...state.accessCardsInfo,
        cards: state.accessCardsInfo?.cards,
        loading: true
      }
    }));

    this.accessCardService.findByUser({ externalUserIds: [this.getState.user?.id] }).subscribe({
      next: (cards: AccessCardFindInterface[]) => {
        this.updateState((state: ExternalUserState) => ({
          accessCardsInfo: {
            ...state.accessCardsInfo,
            cards,
            loading: false
          }
        }));
      }
    });
  }

  public deleteCard(card: AccessCardFindInterface): void {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить карту "${card?.code}"?`,
      accept: () => {
        this.updateState((state: ExternalUserState) => ({
          accessCardsInfo: {
            ...state.accessCardsInfo,
            loadingIcon: true
          }
        }));

        this.accessCardService.deleteById(card?.id).subscribe(() => {
          this.errorService.addSuccessMessage(`Карта "${card?.code}" удалена!`);
          this.updateState((state: ExternalUserState) => ({
            accessCardsInfo: {
              ...state.accessCardsInfo,
              loadingIcon: false
            }
          }));
          this.loadAccessCards();
        });
      }
    });
  }

  public loadAccessControl(): void {
    this.updateState((state: ExternalUserState) => ({
      accessControlInfo: {
        ...state.accessControlInfo,
        settings: state.accessControlInfo?.settings,
        loading: true
      }
    }));

    this.externalUserService.getPacsAccess(this.getState.user?.id).subscribe({
      next: (settings: RoomAccessFindInterface) => {
        this.updateState((state: ExternalUserState) => ({
          accessControlInfo: {
            ...state.accessControlInfo,
            settings,
            loading: false
          }
        }));
      }
    });
  }

  public setAccessControl(settings: RoomAccessFindInterface): void {
    this.updateState((state: ExternalUserState) => ({
      accessControlInfo: {
        ...state.accessControlInfo,
        loadingButton: true
      }
    }));

    this.externalUserService.setPacsAccess(this.getState.user?.id, settings).subscribe({
      next: () => {
        this.updateState((state: ExternalUserState) => ({
          accessControlInfo: {
            ...state.accessControlInfo,
            settings: { ...settings },
            loadingButton: false
          }
        }));
        this.errorService.addSuccessMessage('Изменения сохранены!');
      },
      error: () => {
        this.updateState((state: ExternalUserState) => ({
          accessControlInfo: {
            ...state.accessControlInfo,
            loadingButton: false
          }
        }));
      }
    });
  }

  public updateFieldUser(key: keyof ExternalUserPartialFormInterface, value: any) {
    this.externalUserService.patch({ id: this.getState.user?.id, [key]: value || null }).subscribe();
    const user: ExternalUserFindInterface = { ...this.getState.user, [key]: value || null };
    this.updateState({ user });
  }
}
